<script setup lang="tsx">
  import { PencilIcon } from '@heroicons/vue/solid';
  import { get, post } from '~/services/donationScreen';
  import type { VendorDrawerType, GetResponse, ResponseStatus } from '~/types';
  import Attachments from '../../Others/Attachments.vue';
  import AttachmentUpdates from '../RoutedDrawerComponents/AttachmentUpdates.vue';
  import InfoRow from '~/components/UI/Drawers/InfoRow.vue';
  import InfoSection from '~/components/UI/Drawers/InfoSection.vue';
  import DrawerLayout from '~/components/UI/Drawers/DrawerLayout.vue';
  import OverviewCard from '~/components/UI/Others/OverviewCard.vue';
  import { checkValueAndLength } from '~/functions/stringUtilities';
  import AuditLogs from '../RoutedDrawerComponents/AuditLogs.vue';
  import RoleBasedLinkButton from '~/components/UI/Others/RoleBasedLinkButton.vue';
  import RoleBasedButton from '~/components/UI/Others/RoleBasedButton.vue';
  import DeactivateVendorModal from '../../Modals/DeactivateVendorModal.vue';
  import Approvals from '../RoutedDrawerComponents/Approvals.vue';
  import RejectVendorModal from '../../Modals/RejectVendorModal.vue';
  import ApproveVendorModal from '../../Modals/ApproveVendorModal.vue';
  import ReactivateVendorModal from '../../Modals/ReactivateVendorModal.vue';
  import { formatDate, formatDateWithLocalSlash } from '~/functions/dateTimeUtilities';

  const { identifier, onClose } = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();

  const route = useRoute();
  const deactivateVendorModal = ref(false);
  const reactivateVendorModal = ref(false);
  const approveVendorModal = ref(false);
  const rejectVendorModal = ref(false);
  const approvalStatus = ref<string>('');
  const payeeSubmitForApproval = ref(false);
  const { role } = useAuth();

  const data = ref<VendorDrawerType>({} as VendorDrawerType);
  const responseState = ref<ResponseStatus>('idle');
  const submitForApprovalStatus = ref<ResponseStatus>('idle');

  const fetchData = async () => {
    responseState.value = 'pending';
    try {
      const response = await get<GetResponse<VendorDrawerType>>(`v1/get_vendor/${identifier}`);
      if (response.data.status === 'success') {
        data.value = response.data.data;
        approvalStatus.value = response.data.data?.selected_vendor?.approval_status;
        payeeSubmitForApproval.value = response.data.data?.selected_vendor?.payee_submit_for_approval;
        responseState.value = 'success';
      } else {
        responseState.value = 'error';
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      responseState.value = 'error';
    }
  };

  const vendorPartyName: any = computed(() => {
    const v: any = data.value?.selected_vendor;
    if (!v) return '';
    return `${v.name}-${v.alias_name ? v.alias_name + '-' : ''}${v.vendor_id ? v.vendor_id + '-' : ''}${v.vendor_type}-${v.uid}`;
  });

  const handleSubmitForApproval = async () => {
    submitForApprovalStatus.value = 'pending';
    const identifier = route.query.identifier;
    const response = await post<any>('/v1/submit_vendor_for_approval', {
      data: { uid: identifier }
    });
    try {
      if (response?.data?.status === 'success') {
        fetchData();
        openSuccessfulCustomNotification(response?.data?.message);
        submitForApprovalStatus.value = 'success';
      } else {
        openFailedCustomNotification(response?.data?.message);
        submitForApprovalStatus.value = 'error';
      }
    } catch (error) {
      console.error('some', error);
    }
  };

  const parsedBankDetails = computed(() => {
    const raw = data.value?.selected_vendor?.bank_details;
    if (!raw) return [];

    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

    return Object.values(parsed).map((item: any) => {
      const key = Object.keys(item)
        .find(k => k.startsWith('li_acc_no'))
        ?.split('_')
        .pop();

      return {
        li_acc_no: item[`li_acc_no_${key}`],
        li_name_as_per_bank: item[`li_name_as_per_bank_${key}`],
        li_ifsc: item[`li_ifsc_${key}`],
        li_bank_name: item[`li_bank_name_${key}`],
        li_acc_type: item[`li_acc_type_${key}`],
        is_default: item.is_default
      };
    });
  });

  const parsedContracts = computed(() => {
    const raw = data.value?.selected_vendor?.contracts;
    if (!raw) return [];

    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

    return Object.values(parsed).map((item: any) => ({
      contract_start_date: item.contract_start_date_1,
      contract_end_date: item.contract_end_date_1,
      contract_value: item.contract_value_1,
      contract_status: item.contract_status_1
    }));
  });

  const isEditDisabled = computed(() => {
    const rolesList = role.value || [];
    const isSubmitter = rolesList.includes('payee_submitter') || rolesList.includes('vendor_submitter');
    const isNotSubmitted = approvalStatus.value === 'Not submitted for approval';
    const isApproverRole = rolesList.includes('payee_approver') || rolesList.includes('vendor_approver');
    const isAdmin = rolesList.includes('admin');
    const isPayeeAdmin = rolesList.includes('payee_admin') || rolesList.includes('vendor_admin');
    const isCurrentApprover = data?.value?.selected_vendor?.is_current_approver;
    const isLastApprover = data?.value?.selected_vendor?.is_final_approver;
    const isActiveValue = data?.value?.selected_vendor?.is_active;
    const isApproved = approvalStatus.value === 'Approved';
    const isNoApprover = approvalStatus.value === '0 of 0';
    const isPayeeControlAdmin = rolesList.includes('payee_approval_control_admin');

    if (!isApproved && !isNotSubmitted && !isNoApprover && (isAdmin || isPayeeAdmin) && isPayeeControlAdmin) {
      return false;
    }

    if ((isSubmitter || isAdmin || isPayeeAdmin) && isNoApprover) {
      return isActiveValue ? false : true;
    }

    if (isNotSubmitted && isSubmitter && isActiveValue) {
      return false;
    }

    if (!isNotSubmitted && !isActiveValue && !isApproved) {
      return true;
    }

    if (isSubmitter && isNotSubmitted && !isActiveValue) {
      return true;
    }

    if (isPayeeAdmin && isNotSubmitted) {
      return false;
    }

    if (isPayeeAdmin && isApproved) {
      return false;
    }

    if (isAdmin && isNotSubmitted) {
      return isActiveValue ? false : true;
    }

    if (!isApproved && !isNotSubmitted && isCurrentApprover) {
      return false;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && isCurrentApprover) {
      return true;
    }

    if (isAdmin && isApproved) {
      return false;
    }

    if ((isApproverRole || isPayeeAdmin || isAdmin) && isNotSubmitted && isCurrentApprover) {
      return false;
    }

    if (isCurrentApprover && isNotSubmitted && !isLastApprover) {
      return true;
    }

    if ((isNotSubmitted && isLastApprover) || isCurrentApprover) {
      return true;
    }

    if (isApproved && isLastApprover) {
      return false;
    }

    if (isNotSubmitted && !isActiveValue) {
      return true;
    }

    return true;
  });

  const isSubmitDisabled = computed(() => {
    const rolesList = role.value || [];
    const isCurrentApprover = data?.value?.selected_vendor?.is_current_approver;
    const isSubmitter = rolesList.includes('payee_submitter') || rolesList.includes('vendor_submitter');
    const isNotSubmitted = approvalStatus.value === 'Not submitted for approval';
    const isApproverRole = rolesList.includes('payee_approver') || rolesList.includes('vendor_approver');
    const isAdmin = rolesList.includes('admin');
    const isPayeeAdmin = rolesList.includes('payee_admin') || rolesList.includes('vendor_admin');
    const isLastApprover = data?.value?.selected_vendor?.is_final_approver;
    const isApproved = approvalStatus.value === 'Approved' || approvalStatus.value === '0 of 0';
    const isActiveValue = data?.value?.selected_vendor?.is_active;
    const isPayeeControlAdmin = rolesList.includes('payee_approval_control_admin');

    if (isNotSubmitted && (isPayeeAdmin || isAdmin) && isPayeeControlAdmin) {
      return false;
    }

    if (!isNotSubmitted && !isActiveValue && !isApproved) {
      return true;
    }

    if (isNotSubmitted && !isActiveValue && isSubmitter) {
      return true;
    }

    if (isNotSubmitted && isSubmitter) {
      return false;
    }

    if (isSubmitter && isNotSubmitted) {
      return false;
    }

    if (isCurrentApprover && isNotSubmitted && !isLastApprover) {
      return false;
    }

    if (isSubmitter && isApproved) {
      return true;
    }

    if (isAdmin && isNotSubmitted) {
      return isActiveValue ? false : true;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && isCurrentApprover && isActiveValue) {
      return true;
    }

    if (isPayeeAdmin && isNotSubmitted) {
      return false;
    }

    if ((isApproverRole || isPayeeAdmin || isAdmin) && isNotSubmitted && isCurrentApprover) {
      return false;
    }

    if ((isNotSubmitted && isLastApprover) || isCurrentApprover) {
      return true;
    }

    if (isNotSubmitted && !isActiveValue) {
      return true;
    }

    return true;
  });

  const isDeactivateDisabled = computed(() => {
    const rolesList = role.value || [];
    const isSubmitter = rolesList.includes('payee_submitter') || rolesList.includes('vendor_submitter');
    const isApproverRole = rolesList.includes('payee_approver') || rolesList.includes('vendor_approver');
    const isAdmin = rolesList.includes('admin');
    const isPayeeAdmin = rolesList.includes('payee_admin') || rolesList.includes('vendor_admin');
    const isNotSubmitted = approvalStatus.value === 'Not submitted for approval';
    const isApproved = approvalStatus.value === 'Approved';
    const isCurrentApprover = data?.value?.selected_vendor?.is_current_approver;
    const isActive = data?.value?.selected_vendor?.is_active;
    const isLastApprover = data?.value?.selected_vendor?.is_final_approver;
    const isNoApprover = approvalStatus.value === '0 of 0';

    if ((isSubmitter || isAdmin || isPayeeAdmin) && isNoApprover) {
      return isActive ? false : true;
    }

    if (isNotSubmitted && isSubmitter) {
      return isActive ? false : true;
    }

    if (isSubmitter && !isNotSubmitted && !isApproved) {
      return true;
    }

    if (isNotSubmitted && isPayeeAdmin) {
      return false;
    }

    if (isNotSubmitted && isApproverRole && !isCurrentApprover) {
      return true;
    }

    if (isSubmitter && isApproved) {
      return true;
    }

    if (isPayeeAdmin && isNotSubmitted) {
      return isActive ? false : true;
    }

    if ((isApproverRole || isPayeeAdmin || isAdmin) && isNotSubmitted && isCurrentApprover) {
      return isActive ? false : true;
    }

    if (isAdmin && isNotSubmitted) {
      return isActive ? false : true;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && isCurrentApprover && isActive) {
      return true;
    }

    if (!isApproved && !isNotSubmitted && isCurrentApprover) {
      return false;
    }

    if (!isApproved && !isNotSubmitted && isLastApprover) {
      return true;
    }

    if (isApproved && isLastApprover) {
      return false;
    }

    if (isApproved && isApproverRole) {
      return true;
    }

    if (isApproverRole && isCurrentApprover) return true;

    return !isActive;
  });

  const handleDeactivateVendorModalOpen = () => {
    deactivateVendorModal.value = true;
  };

  const handleReactivateVendorModalOpen = () => {
    reactivateVendorModal.value = true;
  };

  const handleApproveVendorModalOpen = () => {
    approveVendorModal.value = true;
  };

  const handleRejectVendorModalOpen = () => {
    rejectVendorModal.value = true;
  };

  const handleDeactivateVendorModalClose = () => {
    deactivateVendorModal.value = false;
  };

  const handleReactivateVendorModalClose = () => {
    reactivateVendorModal.value = false;
  };

  const handleRejectVendorModalClose = () => {
    rejectVendorModal.value = false;
  };

  const handleApproveVendorModalClose = () => {
    approveVendorModal.value = false;
  };

  const parsedApproversList = computed(() => {
    const raw = data.value?.selected_vendor?.approvers_list;
    if (!raw) return {};

    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  });

  onMounted(fetchData);
</script>

<template>
  <a-drawer
    v-if="identifier"
    class="monetary-drawer"
    :body-style="{ padding: '0px' }"
    :header-style="{
      padding: '12px',
      borderBottom: '1px solid #E4E4E7'
    }"
    title="Payee Information"
    :open="identifier !== null"
    width="1080"
    @close="onClose"
  >
    <template #extra>
      <div class="flex gap-2">
        <RoleBasedButton
          component-name="deactivateVendorButton"
          active-class="text-blue-700 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center text-gray-500 border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          disabled-class="flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          :disabled="data?.selected_vendor?.is_active"
          @click="handleReactivateVendorModalOpen"
        >
          Reactivate Payee
        </RoleBasedButton>
        <RoleBasedButton
          component-name="deactivateVendorButton"
          active-class="text-blue-700 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center text-gray-500 border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          disabled-class="flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          :disabled="isDeactivateDisabled"
          @click="handleDeactivateVendorModalOpen"
        >
          Deactivate Payee
        </RoleBasedButton>
        <RoleBasedLinkButton
          :to="{ path: '/payees/edit-payee', query: { identifier } }"
          component-name="editPayeeButton"
          active-class="text-blue-700 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center text-gray-500 border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          disabled-class="flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          :disabled="isEditDisabled"
        >
          <PencilIcon class="h-4 w-4" :class="isEditDisabled ? 'text-gray-400' : 'text-blue-600'" />
          Edit Payee
        </RoleBasedLinkButton>
        <RoleBasedButton
          component-name="submitVendorButton"
          active-class="!text-white hover:!text-white bg-blue-700 hover:bg-blue-600 disabled:!text-blue-300 disabled:border-blue-300 disabled:bg-blue-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-blue-700 hover:!border-blue-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-blue-500 gap-1 text-sm font-medium py-1.5 px-2 justify-center !bg-blue-100 border !border-blue-500 shadow-[0_1px_2px_0px_#0000000D]"
          :disabled="isSubmitDisabled"
          :loading="submitForApprovalStatus === 'pending'"
          @click="handleSubmitForApproval"
        >
          Submit For Approval
        </RoleBasedButton>
        <RoleBasedButton
          component-name="rejectVendorButton"
          active-class="!text-white hover:!text-white bg-red-700 hover:bg-red-600 disabled:!text-red-300 disabled:border-red-300 disabled:bg-red-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-red-700 hover:!border-red-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-red-300 gap-1 text-sm font-medium py-1.5 px-2 justify-center border !border-red-300 shadow-[0_1px_2px_0px_#0000000D]"
          :disabled="approvalStatus === 'Not submitted for approval' || !data?.selected_vendor?.is_current_approver"
          @click="handleRejectVendorModalOpen"
        >
          Deny Payee
        </RoleBasedButton>
        <RoleBasedButton
          component-name="approverVendorButton"
          active-class="!text-white hover:!text-white bg-green-700 hover:bg-green-600 disabled:!text-green-300 disabled:border-green-300 disabled:bg-green-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-green-700 hover:!border-green-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-green-500 gap-1 text-sm font-medium py-1.5 px-2 justify-center !bg-green-100 border !border-green-500 shadow-[0_1px_2px_0px_#0000000D]"
          :disabled="approvalStatus === 'Not submitted for approval' || !data?.selected_vendor?.is_current_approver"
          @click="handleApproveVendorModalOpen"
        >
          Approve Payee
        </RoleBasedButton>
      </div>
    </template>
    <DrawerLayout
      :response-state="responseState"
      :main-display="data?.selected_vendor?.name"
      :sub-display="data?.selected_vendor?.vendor_type"
      :side-display="''"
      @close="onClose"
    >
      <template #column1>
        <div class="p-3 flex flex-col gap-3">
          <a-alert
            v-if="(approvalStatus === 'Approved' || approvalStatus === '0 of 0') && isEditDisabled"
            message="You are not an access of this payee!"
            type="error"
            show-icon
          />
          <a-alert
            v-else-if="data?.selected_vendor?.is_active && isEditDisabled"
            message="You are not a current approver for this payee!"
            type="error"
            show-icon
          />
        </div>
        <InfoSection custom-class="border-b">
          <InfoRow label="Email" :value="checkValueAndLength(data?.selected_vendor?.email)" />
          <InfoRow label="Phone Number" :value="checkValueAndLength(data?.selected_vendor?.phone)" />
          <InfoRow label="Payee ID" :value="checkValueAndLength(data?.selected_vendor?.vendor_id)" />
          <InfoRow label="Payee Type" :value="checkValueAndLength(data?.selected_vendor?.vendor_type)" />
          <InfoRow label="Expiry Date" :value="checkValueAndLength(data?.selected_vendor?.expiry_date)" />
        </InfoSection>
        <InfoSection title="SPOC Payee" custom-class="border-b pt-2">
          <InfoRow label="Name" :value="checkValueAndLength(data?.selected_vendor?.spoc_vendor_name)" />
          <InfoRow label="Title" :value="checkValueAndLength(data?.selected_vendor?.spoc_vendor_title)" />
          <InfoRow label="Email" :value="checkValueAndLength(data?.selected_vendor?.spoc_vendor_email)" />
          <InfoRow label="Phone Number" :value="checkValueAndLength(data?.selected_vendor?.spoc_vendor_phone)" />
        </InfoSection>
        <InfoSection title="SPOC Internal" custom-class="border-b pt-2">
          <InfoRow label="Name" :value="checkValueAndLength(data?.selected_vendor?.spoc_internal_name)" />
          <InfoRow label="Title" :value="checkValueAndLength(data?.selected_vendor?.spoc_internal_title)" />
          <InfoRow label="Email" :value="checkValueAndLength(data?.selected_vendor?.spoc_internal_email)" />
          <InfoRow label="Phone Number" :value="checkValueAndLength(data?.selected_vendor?.spoc_internal_phone)" />
        </InfoSection>
        <div v-if="data?.selected_vendor?.bank_details?.length > 0" class="py-2">
          <p class="border-b pb-3 px-3 mb-2 text-[#52525B] text-sm font-medium">Bank and Financials</p>

          <div class="px-3">
            <div class="border border-zinc-300 rounded overflow-hidden">
              <table class="w-full border-b border-collapse sm-medium rounded">
                <thead>
                  <tr class="border-b bg-zinc-50">
                    <th class="text-left py-2 px-3 border-r font-medium">Sr</th>
                    <th class="text-left py-2 px-3 border-x font-medium">A/C No.</th>
                    <th class="text-left py-2 px-3 border-x font-medium">Name as per bank</th>
                    <th class="text-left py-2 px-3 border-l font-medium">IFSC & Bank Name</th>
                    <th class="text-left py-2 px-3 border-l font-medium">A/C Type</th>
                    <th class="text-left py-2 px-3 border-l font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(bankDetails, index) in parsedBankDetails" :key="index">
                    <td class="py-2 px-3 border-t border-r">{{ index + 1 }}</td>
                    <td class="py-2 px-3 border-t border-r text-[#000000] font-normal text-sm">
                      {{ bankDetails.li_acc_no ?? '-' }}
                    </td>
                    <td class="py-2 px-3 border-t border-l text-[#000000] font-normal text-sm">
                      {{ bankDetails.li_name_as_per_bank ?? '-' }}
                    </td>
                    <td class="py-2 px-3 border-x border-t">
                      <div>
                        <p class="text-[#000000] font-normal text-sm">{{ bankDetails.li_ifsc ?? '-' }}</p>
                        <p class="text-[#52525B] font-normal text-xs">{{ bankDetails.li_bank_name ?? '-' }}</p>
                      </div>
                    </td>
                    <td class="py-2 px-3 border-t border-l text-[#000000] font-normal text-sm">
                      {{ bankDetails.li_acc_type ?? '-' }}
                    </td>
                    <td class="py-2 px-3 border-t border-l text-[#000000] font-normal text-sm">
                      <div v-if="bankDetails.is_default === 'Yes'" class="w-full">
                        <FontAwesomeIcon
                          icon="circle-check"
                          :style="{ color: '#3B82F6', width: '20px', height: '18px' }"
                        />
                      </div>
                      <div v-else>-</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <InfoSection class="border-b">
          <InfoRow label="PAN" :value="checkValueAndLength(data?.selected_vendor?.pan)" />
          <InfoRow label="% of TDS" :value="checkValueAndLength(data?.selected_vendor?.tds_percentage)" />
          <InfoRow label="Reference" :value="checkValueAndLength(data?.selected_vendor?.reference)" />
          <InfoRow label="GST Number" :value="checkValueAndLength(data?.selected_vendor?.gst)" />
          <InfoRow label="MSME Payee" :value="checkValueAndLength(data?.selected_vendor?.msme_vendor)" />
          <InfoRow label="Udhyam Number" :value="checkValueAndLength(data?.selected_vendor?.udhyan_reg_no)" />
          <!-- <InfoRow label="Default Party Ledger (Non-FCRA)" :value="checkValueAndLength('-')" /> -->
          <!-- <InfoRow label="Default Party Ledger (FCRA)" :value="checkValueAndLength('-')" /> -->
        </InfoSection>
        <div v-if="data?.selected_vendor?.contracts?.length > 0" class="py-2">
          <p class="border-b pb-3 px-3 mb-2 text-[#52525B] text-sm font-medium">Contract Renewal</p>

          <div class="px-3 pb-2">
            <div class="border border-zinc-300 rounded overflow-hidden">
              <table class="w-full border-b border-collapse sm-medium rounded">
                <thead>
                  <tr class="border-b bg-zinc-50">
                    <th class="text-left py-2 px-3 border-r font-medium">Sr</th>
                    <th class="text-left py-2 px-3 border-x font-medium">Start Date</th>
                    <th class="text-left py-2 px-3 border-x font-medium">End Date</th>
                    <th class="text-left py-2 px-3 border-l font-medium">Value</th>
                    <th class="text-left py-2 px-3 border-l font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(contracts, index) in parsedContracts" :key="index">
                    <td class="py-2 px-3 border-t border-r">{{ index + 1 }}</td>
                    <td class="py-2 px-3 border-t border-r text-[#000000] font-normal text-sm">
                      {{ formatDate(contracts.contract_start_date, 'DD/MM/YYYY') ?? '-' }}
                    </td>
                    <td class="py-2 px-3 border-t border-l text-[#000000] font-normal text-sm">
                      {{ formatDate(contracts.contract_end_date, 'DD/MM/YYYY') ?? '-' }}
                    </td>
                    <td class="py-2 px-3 border-x border-t text-[#000000] font-normal text-sm">
                      {{ contracts.contract_value ?? '-' }}
                    </td>
                    <td class="py-2 px-3 border-t border-l text-[#000000] font-normal text-sm">
                      {{ contracts.contract_status ?? '-' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <InfoRow label="Reminder Email List" :value="checkValueAndLength(data?.selected_vendor?.contract_emails)" />
          <InfoRow
            label="Remind Before"
            :value="
              data?.selected_vendor?.contract_days_before
                ? checkValueAndLength(data?.selected_vendor?.contract_days_before.toString())
                : ''
            "
          />
        </div>
        <InfoSection title="Additional Information" class="border-b border-t pt-2">
          <InfoRow label="Payee Description" :value="checkValueAndLength(data?.selected_vendor?.description)" />
          <InfoRow
            label="Payee Valid Upto"
            :value="
              data?.selected_vendor?.expiry_date ? formatDateWithLocalSlash(data?.selected_vendor?.expiry_date) : '-'
            "
          />
          <InfoRow
            label="Validity Reminder Emails List"
            :value="checkValueAndLength(data?.selected_vendor?.vendor_validity_emails)"
          />
          <InfoRow
            label="Validity Reminder Status"
            :value="data?.selected_vendor?.validity_status ? data?.selected_vendor?.validity_status : '-'"
          />
          <InfoRow
            label="Reminder Days Before"
            :value="checkValueAndLength(data?.selected_vendor?.contract_days_before?.toString())"
          />
          <InfoRow label="KYC Completed" :value="checkValueAndLength(data?.selected_vendor?.kyc_completed)" />
        </InfoSection>
      </template>
      <template #column2>
        <Attachments :items="data?.selected_vendor?.attachments" :table="data?.selected_vendor?.table" />
        <OverviewCard title="Payee Quick Links">
          <div class="flex flex-col gap-4">
            <div class="flex justify-between items-center">
              <div class="flex gap-2 items-center">
                <p
                  class="flex h-5 w-5 shrink-0 grow-0 items-center justify-center rounded-full bg-zinc-200 text-zinc-700 xs-medium"
                >
                  {{ data?.selected_vendor?.quick_links.expense_count }}
                </p>
                <p class="sm-medium text-zinc-500">Expenses</p>
              </div>
              <div class="flex gap-3 items-center">
                <nuxt-link
                  :to="{
                    path: '/expenses',
                    query: { party_name: vendorPartyName }
                  }"
                  :class="
                    data?.selected_vendor?.quick_links.expense_count === '0'
                      ? 'pointer-events-none opacity-50 cursor-not-allowed text-gray-400'
                      : 'text-blue-700'
                  "
                >
                  View
                </nuxt-link>
                <!-- <nuxt-link
                  :to="{ path: '/expenses/add-new-expense', query: { vendorId: identifier } }"
                  class="p-0 border-none shadow-none !bg-transparent text-blue-700"
                >
                  <PlusIcon class="w-5 h-5" />
                </nuxt-link> -->
              </div>
            </div>
            <div class="flex justify-between items-center">
              <div class="flex gap-2 items-center">
                <p
                  class="flex h-5 w-5 shrink-0 grow-0 items-center justify-center rounded-full bg-zinc-200 text-zinc-700 xs-medium"
                >
                  {{ data?.selected_vendor?.quick_links.recurring_count }}
                </p>
                <p class="sm-medium text-zinc-500">Recurring Expenses</p>
              </div>
              <div class="flex gap-3 items-center">
                <nuxt-link
                  :to="{
                    path: '/expenses',
                    query: { party_name: vendorPartyName }
                  }"
                  :class="
                    data?.selected_vendor?.quick_links.recurring_count === '0'
                      ? 'pointer-events-none opacity-50 cursor-not-allowed text-gray-400'
                      : 'text-blue-700'
                  "
                >
                  View
                </nuxt-link>
                <!-- <nuxt-link
                  :to="{ path: '/expenses/add-new-expense', query: { vendorId: identifier } }"
                  class="p-0 border-none shadow-none !bg-transparent text-blue-700"
                >
                  <PlusIcon class="w-5 h-5" />
                </nuxt-link> -->
              </div>
            </div>
            <div class="flex justify-between items-center">
              <div class="flex gap-2 items-center">
                <p
                  class="flex h-5 w-5 shrink-0 grow-0 items-center justify-center rounded-full bg-zinc-200 text-zinc-700 xs-medium"
                >
                  {{ data?.selected_vendor?.quick_links.advance_count }}
                </p>
                <p class="sm-medium text-zinc-500">Advances</p>
              </div>
              <div class="flex gap-3 items-center">
                <nuxt-link
                  :to="{
                    path: '/advances',
                    query: { advance_uid: data?.selected_vendor?.quick_links.advance_uids.join(',') }
                  }"
                  :class="
                    data?.selected_vendor?.quick_links.advance_count === '0'
                      ? 'pointer-events-none opacity-50 cursor-not-allowed text-gray-400'
                      : 'text-blue-700'
                  "
                >
                  View
                </nuxt-link>
                <!-- <nuxt-link
                  :to="{ path: '/advances/add-new-advance', query: { vendorId: identifier } }"
                  class="p-0 border-none shadow-none !bg-transparent text-blue-700"
                >
                  <PlusIcon class="w-5 h-5" />
                </nuxt-link> -->
              </div>
            </div>
          </div>
        </OverviewCard>
        <Approvals
          :items="parsedApproversList"
          :creator="data?.selected_vendor?.creator.email"
          :draft-status="data?.selected_vendor?.draftStatus"
          status-of="vendor"
        />
        <AuditLogs :items="data?.audit_logs" title="Vendor" />
        <AttachmentUpdates :items="data?.attachment_logs" />
      </template>
    </DrawerLayout>
  </a-drawer>
  <DeactivateVendorModal
    :visible="deactivateVendorModal"
    :vendor-id="identifier || ''"
    :close="handleDeactivateVendorModalClose"
  />
  <ApproveVendorModal
    :visible="approveVendorModal"
    :vendor-id="identifier || ''"
    :close="handleApproveVendorModalClose"
  />
  <RejectVendorModal :visible="rejectVendorModal" :vendor-id="identifier || ''" :close="handleRejectVendorModalClose" />
  <ReactivateVendorModal
    :visible="reactivateVendorModal"
    :vendor-id="identifier || ''"
    :close="handleReactivateVendorModalClose"
  />
</template>

<style>
  @import '~/assets/css/donationDrawer.css';
</style>
